import * as React from 'react';
import FormSection from 'Components/FormSection';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import Unit from 'Components/Unit';

const yesNoUnknown = ['yes', 'no', 'unknown'];

const CSFExamform = ({ formData }: { formData: IFormData<IDocumentWithTypeV2<'csfExam'>> }) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <FormSection>
      <FormRow title="general.date">
        <InputHandler editing={true} name="date" type="PartialDate" formData={formData} dateDefault="now" />
      </FormRow>
      <FormRow title="polyneuroDiagnosticExams.csfExam.albuminocytologicDissociation">
        <InputHandler
          name="albuminocytologicDissociation"
          type="Radio"
          editing={true}
          options={['yes', 'no', 'notAssessable', 'unknown']}
          optionFormatter={o => fm(`polyneuroDiagnosticExams.csfExam.opts.${o}`)}
          formData={formData}
        />
      </FormRow>
      <FormRow title="polyneuroDiagnosticExams.csfExam.oligoclonalBands">
        <InputHandler
          name="oligoclonalBands"
          type="Radio"
          editing={true}
          options={['positive', 'negative', 'notTested', 'unknown']}
          optionFormatter={o => fm(`polyneuroDiagnosticExams.csfExam.opts.${o}`)}
          formData={formData}
        />
      </FormRow>
      <FormRow title="polyneuroDiagnosticExams.additionalInformation">
        <InputHandler
          name="additionalInformation"
          type="TextArea"
          editing={true}
          formData={formData}
          placeholder={'polyneuroDiagnosticExams.additionalInformation'}
        />
      </FormRow>
    </FormSection>
  );
};

const NerveBiopsyform = ({ formData }: { formData: IFormData<IDocumentWithTypeV2<'nerveBiopsy'>> }) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <>
      <FormSection>
        <FormRow title="general.date">
          <InputHandler editing={true} name="date" type="PartialDate" formData={formData} dateDefault="now" />
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.nerveBiopsy.biopsiedNerve">
          <InputHandler
            name="biopsiedNerve"
            type="Radio"
            editing={true}
            options={['suralNerve', 'superficialPeronealNerve', 'other']}
            optionFormatter={o => fm(`polyneuroDiagnosticExams.nerveBiopsy.opts.${o}`)}
            formData={formData}
            optionSpecificElements={[
              {
                other: (
                  <InputHandler
                    name="biopsiedNerveOther"
                    type="TextField"
                    editing={true}
                    formData={formData}
                    placeholder={'polyneuroDiagnosticExams.nerveBiopsy.biopsiedNerveOther'}
                  />
                ),
              },
            ]}
            dependentFieldsList={() => ['biopsiedNerveOther']}
            dependentFieldsRemovalWarning
          />
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.nerveBiopsy.interpretation">
          <InputHandler
            name="interpretation"
            type="Radio"
            editing={true}
            options={[
              'supportsInflammatoryNeuropathy',
              'supportsVasculiticNeuropathy',
              'supportsAmyloidNeuropathy',
              'supportsHereditaryNeuropathy',
              'nonDiagnostic',
              'other',
              'unknown',
            ]}
            optionFormatter={o => fm(`polyneuroDiagnosticExams.nerveBiopsy.opts.${o}`)}
            formData={formData}

            optionSpecificElements={[
              {
                other: (
                  <InputHandler
                    name="interpretationOther"
                    type="TextField"
                    editing={true}
                    formData={formData}
                    placeholder={'polyneuroDiagnosticExams.nerveBiopsy.interpretationOther'}
                  />
                ),
              },
            ]}
            dependentFieldsList={() => ['interpretationOther']}
            dependentFieldsRemovalWarning
          />
        </FormRow>
      </FormSection>
      <FormSection header="polyneuroDiagnosticExams.nerveBiopsy.keyFindings">
        <FormRow title="polyneuroDiagnosticExams.nerveBiopsy.amyloidDeposits">
          <InputHandler
            name="amyloidDeposits"
            type="Radio"
            editing={true}
            options={yesNoUnknown}
            optionFormatter={o => fm(`general.${o}`)}
            formData={formData}
          />
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.nerveBiopsy.inflammatoryCellInfiltrates">
          <InputHandler
            name="inflammatoryCellInfiltrates"
            type="Radio"
            editing={true}
            options={yesNoUnknown}
            optionFormatter={o => fm(`general.${o}`)}
            formData={formData}
          />
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.nerveBiopsy.vasculitis">
          <InputHandler
            name="vasculitis"
            type="Radio"
            editing={true}
            options={yesNoUnknown}
            optionFormatter={o => fm(`general.${o}`)}
            formData={formData}
          />
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.nerveBiopsy.demyelination">
          <InputHandler
            name="demyelination"
            type="Radio"
            editing={true}
            options={yesNoUnknown}
            optionFormatter={o => fm(`general.${o}`)}
            formData={formData}
          />
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.nerveBiopsy.axonalLoss">
          <InputHandler
            name="axonalLoss"
            type="Radio"
            editing={true}
            options={yesNoUnknown}
            optionFormatter={o => fm(`general.${o}`)}
            formData={formData}
          />
        </FormRow>
      </FormSection>
      <FormSection>
        <FormRow title="polyneuroDiagnosticExams.additionalInformation">
          <InputHandler
            name="additionalInformation"
            type="TextArea"
            editing={true}
            formData={formData}
            placeholder={'polyneuroDiagnosticExams.additionalInformation'}
          />
        </FormRow>
      </FormSection>
    </>
  );
};

const SkinBiopsyform = ({ formData }: { formData: IFormData<IDocumentWithTypeV2<'skinBiopsy'>> }) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <>
      <FormSection>
        <FormRow title="general.date">
          <InputHandler editing={true} name="date" type="PartialDate" formData={formData} dateDefault="now" />
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.skinBiopsy.biopsySite">
          <InputHandler
            name="biopsySite"
            type="Radio"
            editing={true}
            options={['distalLowerLeg', 'proximalThigh', 'other']}
            optionFormatter={o => fm(`polyneuroDiagnosticExams.skinBiopsy.opts.${o}`)}
            formData={formData}
            optionSpecificElements={[
              {
                other: (
                  <InputHandler
                    name="biopsySiteOther"
                    type="TextField"
                    editing={true}
                    formData={formData}
                    placeholder={'polyneuroDiagnosticExams.skinBiopsy.biopsySiteOther'}
                  />
                ),
              },
            ]}
            dependentFieldsList={() => ['biopsySiteOther']}
            dependentFieldsRemovalWarning
          />
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.skinBiopsy.intraepidermalNerveFiberDensity">
          <Unit unit={fm('polyneuroDiagnosticExams.skinBiopsy.fibersMM')}>
            <InputHandler
              name="intraepidermalNerveFiberDensity"
              type="NumberField"
              editing={true}
              formData={formData}
              placeholder={'polyneuroDiagnosticExams.skinBiopsy.fibersMM'}
            />
          </Unit>
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.skinBiopsy.belowReferenceValue">
          <InputHandler
            name="belowReferenceValue"
            type="Radio"
            editing={true}
            options={yesNoUnknown}
            optionFormatter={o => fm(`general.${o}`)}
            formData={formData}
          />
        </FormRow>
        <FormRow title="polyneuroDiagnosticExams.additionalInformation">
          <InputHandler
            name="additionalInformation"
            type="TextArea"
            editing={true}
            formData={formData}
            placeholder={'polyneuroDiagnosticExams.additionalInformation'}
          />
        </FormRow>
      </FormSection>
    </>
  );
};

const DiagnosticTestsForm = ({ formData, type }: IOwnProps): React.JSX.Element => {
  return type === 'csfExam' ? (
    <CSFExamform formData={formData} />
  ) : type === 'nerveBiopsy' ? (
    <NerveBiopsyform formData={formData} />
  ) : type === 'skinBiopsy' ? (
    <SkinBiopsyform formData={formData} />
  ) : (
    <></>
  );
};

interface IOwnProps {
  formData: IFormData<
    IDocumentWithTypeV2<'csfExam'> | IDocumentWithTypeV2<'nerveBiopsy'> | IDocumentWithTypeV2<'skinBiopsy'>
  >;
  type: 'csfExam' | 'nerveBiopsy' | 'skinBiopsy';
}

export default DiagnosticTestsForm;
